/**
 * O transporte em TEMPO REAL — [spec-008 T030], US-2/US-3. WebRTC NATIVO do
 * navegador ↔ provedor, com a troca de SDP passando pelo NOSSO servidor
 * (`api/apiVoz.js::abrirSessaoTempoReal`). Implementa `TransporteDeVoz`
 * (`contracts/transporte.md`) e se registra no ponto único de seleção
 * (`transporte-de-voz.js`), atrás da bandeira.
 *
 * ## O que este arquivo faz — e o que ele NÃO faz
 *
 * Ele ABRE o canal, entrega texto/estado para cima e fecha registrando o
 * consumo. A **delegação** (o modo `responses`, o laço `function_call` →
 * `POST /chat` → `function_call_output` e as três regras medidas do G-13)
 * chega por este mesmo canal e mora em `transporte-tempo-real-delegacao.js`
 * ([T032]) — daqui ela recebe só o que é do transporte: a saída para o fio, o
 * `id_sessao`, a superfície e o `delegar` do `ContextoDeSessao`.
 *
 * ## Nenhuma dependência nova (requisito da task)
 *
 * `RTCPeerConnection` é API do navegador; o microfone vem de
 * `captura-de-voz.js`, o mesmo do caminho por turnos — e as constraints ricas
 * dele (echo cancellation, noise suppression) valem ainda mais aqui, onde o
 * alto-falante toca a IA com o microfone aberto.
 *
 * ## ★ O contrato do provedor foi MEDIDO, não copiado de doc
 *
 * Os nomes de evento, os motivos e os dois prazos vivem no irmão
 * `transporte-tempo-real-protocolo.js`, com a tabela de procedência de cada
 * fato (7 sessões WebRTC reais, 812 eventos servidor→cliente). Leia-o antes
 * de acrescentar handler: evento que não está lá não foi observado.
 *
 * Dois fatos de lá decidem desenho AQUI: **não existe** `*.done` de
 * transcrição nem evento de "parei de falar" — daí as TRÊS DERIVAÇÕES, cuja
 * tabela (com o fato de origem de cada uma) mora no cabeçalho de
 * `transporte-tempo-real-eventos.js`, que é quem as implementa; e **não
 * existe** evento de cancelar fala cliente→servidor, daí o `interromper`.
 *
 * ## Um evento a MAIS no `Destino`: `audioRemoto`
 *
 * A tabela do contrato não o lista, mas a descrição do `Destino` diz "onde o
 * transporte entrega texto transcrito, **áudio** e eventos de estado" — e o
 * áudio de volta é uma `MediaStream`, não um data channel. `audioRemoto
 * ({stream})` é a entrega dela. Ele existe aqui e não no `porTurnos` porque
 * lá o áudio é um `<audio src=base64>` por frase; aqui é um fluxo contínuo.
 * Quem o anexa a um elemento é a camada de TELA — este módulo fica sem DOM,
 * no mesmo espírito do núcleo de medição.
 *
 * ⚠️ **Viés declarado da medição:** `fim_da_fala` é marcado no último
 * `input_transcript.delta` recebido, que chega DEPOIS de o consultor parar de
 * falar (latência de STT + rede) — o delta medido fica MENOR que o real. O
 * caminho por turnos tem viés do mesmo sinal (marca em `parar()`, depois do
 * último som), então a comparação da US-7 não é desequilibrada por isto. Há
 * uma medição melhor disponível e não usada aqui: os dois eventos de
 * transcrição carregam `start_ms`/`end_ms` na linha do tempo do PROVEDOR, o
 * que daria um delta imune a jitter de rede — fica registrado para a [T042],
 * que é a dona da régua.
 *
 * ## ⚠️ O que este transporte NÃO escreve em `motorDeVoz` — e por quê
 *
 * `transporte-por-turnos.js` instala DOIS `$watch` no estado reativo
 * compartilhado: `estadoDaFala` (liga a escuta de barge-in LOCAL) e
 * `sessaoDeVozAtiva` (liga o observador de silêncio). Os dois são criados na
 * carga do módulo, porque `transporte-de-voz.js` instancia o transporte por
 * turnos de imediato. Se o canal em tempo real escrevesse nesses dois campos,
 * o barge-in local subiria junto — **dois juízes do mesmo turno, por um
 * objeto compartilhado**, que é o invariante N4 quebrado sem ninguém ter
 * chamado `vad-de-voz.js`. Por isso aqui só entram os campos SEM observador:
 * `gravando`, `modoDeVoz` e `microfoneMutado`.
 *
 * ★ **Isto não é hipótese — foi MEDIDO por mutação** (T030): acrescentar
 * `motorDeVoz.estadoDaFala = 'ia'` à abertura deste transporte fez, na mesma
 * suíte, o `criarEscutaDeBargeIn` do v1 ser ARMADO e o `marcarInicioDeFala`
 * (a marca posicional do v1) ser chamado — sem nenhuma linha nova importando
 * `barge-in-de-voz.js`. O teste `★ CONTROLE — escrever estadoDaFala ARMA a
 * escuta do v1` guarda o gatilho, e o par dele guarda a ausência.
 *
 * ★ **RESOLVIDO na [T030b], e o desenho aqui NÃO mudou:** quem desarma os
 * dois `$watch` é o próprio `porTurnos`, perguntando a
 * `transporte-no-comando.js` quem abriu a sessão corrente; e quem escreve
 * `sessaoDeVozAtiva` para a tela desenhar é a FACHADA (`motor-de-voz.js`),
 * depois de o comando já estar com este transporte. Este arquivo segue sem
 * encostar nos campos observados — o acoplamento continua REAL, só que agora
 * tem uma guarda e um par de testes de controle em vez de um aviso.
 */
import { apiVoz } from '@modules/aawz-intelligence-v2/api/apiVoz'
import { pedirMicrofoneComFallback, liberarStream } from './captura-de-voz'
import { motorDeVoz } from './estado-da-voz'
import { registrarTransporteTempoReal } from './transporte-de-voz'
import { TRANSPORTE_TEMPO_REAL } from './medicao-de-voz'
import { marcarFalhaDaMedicao } from './instrumentacao-de-medicao'
import { criarLeitorDeEventosDaSessao } from './transporte-tempo-real-eventos'
import { criarEscutaDeBargeInDoTempoReal } from './transporte-tempo-real-barge-in'
import { criarPorteiroDoMicrofone } from './transporte-tempo-real-porteiro'
import { criarCanalWebRtc } from './transporte-tempo-real-canal'
import { criarVigiaDeLimites } from './transporte-tempo-real-limites'
import { conferirQueAFalaRemete } from './transporte-tempo-real-remissoes'
import {
  ErroDeVozTempoReal,
  EVENTO_APPEND_DE_INSTRUCOES,
  EVENTO_FECHAR_SESSAO,
  MOTIVO_CANAL_CAIU,
  MOTIVO_MICROFONE,
  MOTIVO_SEM_SUPERFICIE,
  MOTIVO_SEM_SUPORTE,
  MOTIVO_SESSAO_RECUSADA,
  montarAppendDeFala,
  montarPedidoDeResposta,
  suportaWebRtc,
} from './transporte-tempo-real-protocolo'

// Reexportado para quem consome o transporte não precisar saber que o
// protocolo mora num irmão — mesma cortesia que `transporte-de-voz.js` faz
// com os nomes de transporte.
export * from './transporte-tempo-real-protocolo'

const _relogio = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()

/**
 * ★ [spec-008 T062] A SAUDAÇÃO — a IA abre a conversa em vez de esperar.
 *
 * Hoje o consultor entra no Modo de Voz e encara o silêncio: o microfone
 * abriu, a sessão está correndo (e sendo cobrada), e nada na tela nem no
 * alto-falante confirma que ele foi ouvido. Quem fala primeiro é ela.
 *
 * ## O texto é PROVISÓRIO e é aqui que se troca
 *
 * Declarado pelo owner em 17/09 ("depois podemos melhorar isso"). Mora numa
 * constante só, nesta função, e é o único lugar a mexer.
 *
 * ## ★ O nome vem da SESSÃO, nunca do modelo (N21)
 *
 * `nome` chega pelo `ContextoDeSessao`, lido do usuário logado
 * (`send-message.vue::contextoDeVoz` → `$currentUser().nome`). **Não se pede
 * o nome ao modelo, e nem se deixa que ele o preencha**: é a mesma deriva que
 * o N21 mede no roteador do provedor — `Teste Alfa` virou `"Teste Alpha"` em
 * 7 de 8 medições (G-12 §6 + G-13 §3). Um "Olá, João Pedro" para o Pedro João
 * queima a confiança da sessão inteira na primeira frase.
 *
 * **Sem nome, a saudação sai SEM nome** — nunca com placeholder, nunca com
 * um nome inventado. Cumprimentar sem nome é educado; cumprimentar o nome
 * errado, não.
 *
 * O primeiro nome, e não o nome inteiro: "Olá, João Gabriel John, em que
 * posso ajudá-lo?" é um formulário lido em voz alta, não um cumprimento.
 *
 * @param {string} [nome] o nome do consultor, como a sessão o conhece.
 * @returns {string} a frase, exatamente como deve ser dita.
 */
export const saudacaoDeAbertura = (nome) => {
  const primeiroNome = String(nome || '')
    .trim()
    .split(/\s+/)[0]
  if (!primeiroNome) return 'Olá, em que posso ajudá-lo?'
  return `Olá, ${primeiroNome}, em que posso ajudá-lo?`
}

/**
 * ★ [T062] A instrução que leva a saudação ao canal.
 *
 * `session.instructions.append` com `delegation_id: null` é o caminho MEDIDO
 * (G-13 §4, sondagem B): voltou `session.instructions.appended` e o modelo
 * falou, ecoando quase literalmente o conteúdo enviado. O `response.create`
 * que a acompanha é o que faz o turno EXISTIR — as 7 sessões medidas nunca
 * mostraram o modelo começando turno sozinho (§3 do `-remissoes.js`: elas
 * provam que ele responde, nunca que ele abre).
 *
 * "exatamente" e "nada além" porque o modelo REFORMULA o que recebe (G-12 §6)
 * — e esta frase em particular carrega o nome de uma pessoa.
 */
const _instrucaoDaSaudacao = (nome) =>
  'Antes de qualquer outra coisa, cumprimente o consultor dizendo ' +
  `exatamente esta frase e nada além dela: "${saudacaoDeAbertura(nome)}"`

/**
 * ★ [spec-008 T073] ACIONE PRIMEIRO, FALE DEPOIS — a regra que faz a consulta
 * acontecer. Ela substitui a instrução de ANUNCIAR (T072), e a troca foi
 * MEDIDA, não argumentada.
 *
 * ## O defeito que a redação anterior produzia
 *
 * A instrução antiga mandava *"diga em UMA frase curta que você vai buscar
 * agora e, no MESMO turno, acione a ferramenta"*. Em 4 sessões o modelo dizia
 * a frase e **nunca emitia o `function_call`**: o consultor ouvia *"Vou
 * verificar"* e a tela ficava vazia. Isto é PIOR que o silêncio que a
 * instrução veio corrigir — é uma promessa que ninguém cumpre.
 *
 * ## O experimento (`tests/e2e-playwright/specs/voz-anuncia-e-nao-aciona.spec.js`)
 *
 * 26 sessões de tempo real REAIS, 13 por braço, alternadas A,B,A,B, mesmo
 * bundle, mesma pergunta (*"Quantas tarefas eu tenho em aberto?"*). A troca da
 * redação acontece NO FIO — os dois braços rodam o mesmo código e o fio
 * testemunha qual texto saiu:
 *
 * | braço | acionou | falhou | mediana até acionar |
 * |---|---|---|---|
 * | anunciar primeiro (o antigo) | 9/13 | **4** | 1.981 ms |
 * | **acionar primeiro (este)** | **13/13** | **0** | 1.833 ms |
 *
 * Fisher exato unilateral: **p = 0,048**. As quatro falhas são todas o mesmo
 * desfecho — *"Vou buscar essa informação agora."* e nada mais.
 *
 * ⚠️ **O p é otimista, e isto tem de ficar escrito.** A amostra foi ampliada
 * depois de olhadas parciais (2 → 5 → 10 → 13 por braço); só o último bloco
 * foi declarado antes de ser visto. O que o dado sustenta com folga é o
 * SENTIDO — o braço novo nunca falhou em 13 e é ~150 ms mais rápido —, não a
 * casa decimal do p.
 *
 * ## Por que dá para tirar o anúncio agora, e não dava antes
 *
 * Porque quem conversa durante a espera passou a ser a APLICAÇÃO: com
 * `DELEGACAO_EM_DOIS_TEMPOS` o laço fecha no disparo e o acompanhamento é
 * aceito pelo provedor (1 append → 1 eco). Sem ela, tirar o anúncio
 * devolveria o silêncio que a T072 veio corrigir — as duas mudanças são uma
 * só, e desligar a dos dois tempos reabre este defeito.
 *
 * ## O que continua valendo da T072
 *
 * - **Vai no MESMO append da saudação**, e ANTES dela: instrução de sessão é
 *   contexto persistente, e o *"nada além dela"* da saudação tem de se referir
 *   ao cumprimento, não engolir a regra dos turnos seguintes.
 * - **Vai na ABERTURA**, porque durante a espera não funciona: com a delegação
 *   aberta o provedor descarta todo `session.*.append` (26 enviados, 0
 *   ecoados — [T071]).
 * - ⚠️ **É direção de COMPORTAMENTO, não texto a ser lido.** Nenhuma frase
 *   entre aspas: congelá-la faria a voz repetir a mesma coisa a cada pergunta.
 *   O único literal da abertura continua sendo a saudação, que carrega o nome
 *   de uma pessoa (N21).
 *
 * ⚠️ **O modelo ainda fala antes de acionar em 9 dos 13** — *"Já estou
 * consultando."*, *"Uhum."* — e isso é ACEITÁVEL: o que a instrução compra não
 * é o silêncio, é o acionamento. Em 4 das 13 ele ficou mudo e acionou direto,
 * que é o desenho; nas outras 9 falou E acionou. Nenhuma ficou só na fala.
 */
/*
 * ★★★ [T111] A ÚLTIMA FRASE MUDOU — e a antiga era metade do defeito do G6.
 *
 * Ela dizia *"quem conversa com o consultor durante a espera é a aplicação,
 * não você. Depois de acionar, **espere calado até o resultado chegar**"*. E
 * nós entregávamos, como resultado da ferramenta, um `{status:
 * 'em_andamento'}` que dizia *"você NÃO recebeu resultado nenhum"*.
 *
 * ★ Do ponto de vista do modelo, **o resultado nunca chegava**. Ele não estava
 * engolindo os nossos eventos: estava obedecendo. Na retomada medida aos 109 s
 * ele gera exatamente o que mandamos — *"já disparei a consulta e ela ainda
 * está em andamento"*.
 *
 * Com o resultado real voltando pelo `function_call_output` ([T111]), "o
 * resultado chegar" passa a ser um fato verificável na conversa dele, e a
 * instrução pode dizer o que o dono do produto descreveu: a voz é o
 * INTERMEDIÁRIO entre o consultor e o agente.
 *
 * ⚠️ O que NÃO muda: acionar primeiro (é o que a medição de 13 execuções
 * comprou) e não inventar dado.
 */
export const INSTRUCAO_DE_ACIONAR_PRIMEIRO =
  'Nesta conversa você é o INTERMEDIÁRIO entre o consultor e o assistente do ' +
  'CRM da AAWZ, que é quem tem os dados. Sempre que o pedido exigir buscar ' +
  'dados na plataforma, acione a ferramenta IMEDIATAMENTE, como a primeira ' +
  'coisa do turno, ANTES de dizer qualquer palavra: não peça permissão e não ' +
  'espere o consultor responder. A consulta pode levar minutos; enquanto ela ' +
  'corre, você pode conversar normalmente com o consultor, e a aplicação ' +
  'também vai avisando. Quando o resultado da ferramenta chegar, FALE-O ao ' +
  'consultor na hora, com as suas palavras e em voz alta, seguindo a ' +
  'instrução que vier junto do resultado. Nunca invente valor, nome, data ou ' +
  'período que não esteja no resultado.'

/**
 * As instruções da abertura, na ordem em que valem. Uma função só porque elas
 * saem juntas, num único `session.instructions.append`.
 */
const _instrucoesDaAbertura = (nome) =>
  `${INSTRUCAO_DE_ACIONAR_PRIMEIRO} ${_instrucaoDaSaudacao(nome)}`

/**
 * Fábrica do transporte em tempo real. Uma instância por sessão de módulo —
 * memoizada por `transporte-de-voz.js`, mesma regra do `porTurnos`.
 */
// eslint-disable-next-line max-statements
export const criarTransporteTempoReal = () => {
  // Nada disto é estado de tela — são instâncias e buffers do canal.
  let _stream = null
  let _escutaDeBargeIn = null
  let _porteiro = null
  let _destino = null
  let _idSessao = null
  let _modelo = null
  let _tetoSegundos = null
  // [T032] O que a delegação precisa do `ContextoDeSessao` (C-5: a superfície
  // viaja em TODA delegação, não só na abertura).
  let _superficie = null
  let _delegar = null
  // ★ [T065] O getter do valor JÁ EM MÃOS (D-O-5). Guardado como o `_delegar`
  // — por sessão, e nunca herdado: ele lê o store de UMA conversa.
  let _lerValorParcial = null
  let _abertaEm = null
  let _abrindo = false
  let _fechamentoPedido = false
  // ★ [T062] O nome do consultor, como a SESSÃO o conhece — e a marca de que
  // ela já foi cumprimentada. `_saudou` é POR SESSÃO: zerado na abertura
  // (`_prepararAbertura`), então reabrir depois de encerrar cumprimenta de
  // novo, e nenhum evento do meio da sessão cumprimenta uma segunda vez.
  let _nomeDoConsultor = ''
  let _saudou = false

  const emitir = (evento, payload) => {
    if (!_destino || typeof _destino[evento] !== 'function') return
    _destino[evento](payload || {})
  }


  const registrarDestino = (destino) => {
    _destino = destino
  }

  const desregistrarDestino = (destino) => {
    if (_destino === destino) _destino = null
  }

  // A tradução evento-do-canal → evento-do-`Destino` + marcas mora no irmão
  // `transporte-tempo-real-eventos.js`: aqui fica o que fala WebRTC/HTTP, lá o
  // que interpreta o fio. `pediuFechamento` é o que distingue "encerrou" de
  // "caiu", e só este arquivo sabe disso.
  const _leitor = criarLeitorDeEventosDaSessao({
    emitir,
    pediuFechamento: () => _fechamentoPedido,
    // [T032] Funções, não valores: as duas nascem depois deste ponto.
    // [T063] `origem` só vem na fala que adianta número já em mãos.
    enviar: (mensagem, origem) => _enviarNoCanal(mensagem, origem),
    contexto: () => ({
      idSessao: _idSessao,
      superficie: _superficie,
      delegar: _delegar,
      // ★ [T065] A fiação que faltava para a D-O-5 sair do papel: sem esta
      // chave, `_valorParcialDoTurno` do executor não acha getter nenhum,
      // devolve `null` e TODO adiantamento é recusado — degradação segura, e
      // decisão do owner inerte. A leitura é do CONTEXTO, não deste módulo:
      // quem sabe ler o cartão é a tela.
      lerValorParcialDoTurno: _lerValorParcial,
    }),
    // [T035] O tique do ledger nativo vai ao vigia do teto.
    aoConsumo: (segundos) => _vigia.aoAtualizarConsumo(segundos),
  })

  /**
   * [T035] Os dois limites da sessão paga (teto de duração pelo ledger do
   * provedor; perda de foco prolongada). Ele AVISA e, se ninguém reagir, pede
   * o encerramento — que passa pelo `fechar` de sempre, com o consumo.
   */
  const _vigia = criarVigiaDeLimites({
    avisar: (aviso) => emitir('avisoDeEncerramento', aviso),
    encerrar: (motivo) => _encerrarPorLimite(motivo),
    // ★★★ [T112] O corte por aba escondida (3 min) é mais curto que uma
    // consulta ao agente (1 a 4 min). Enquanto houver chamada esperando
    // resultado, ele não acontece — o teto de CUSTO da sessão segue intacto.
    consultaEmVoo: () => _leitor.delegacaoEmVoo(),
  })

  /**
   * Queda do transporte de mídia. Só `failed` (terminal por especificação) e
   * `closed` sem termos pedido contam: `disconnected` é transitório e pode
   * voltar sozinho — emitir `queda` nele derrubaria a sessão num soluço de
   * rede. N6: este transporte não renova nada; caiu, quem chama abre de novo.
   */
  const _aoMudarConexao = (estado) => {
    if (_fechamentoPedido) return
    if (estado !== 'failed' && estado !== 'closed') return
    marcarFalhaDaMedicao(MOTIVO_CANAL_CAIU)
    emitir('queda', { motivo: estado })
  }

  /**
   * Os três acessórios da sessão caem JUNTOS, e a ordem aqui é a que importa:
   * antes de `liberarStream`, porque o medidor de nível vive sobre ESTE stream
   * e pará-lo depois deixaria um `AudioContext` lendo um dispositivo solto; e
   * o porteiro antes do barge-in, porque o `parar()` dele REABRE a trilha e
   * ele nunca pode ser o motivo de um microfone mudo depois da sessão.
   */
  const _desarmarAcessorios = () => {
    if (_porteiro) {
      _porteiro.parar()
      _porteiro = null
    }
    if (_escutaDeBargeIn) {
      _escutaDeBargeIn.parar()
      _escutaDeBargeIn = null
    }
  }

  const _desmontar = () => {
    _leitor.limpar()
    _vigia.parar()
    _desarmarAcessorios()
    _cano.fechar()
    // Solta o microfone SEMPRE, inclusive quando a abertura falhou no meio:
    // o LED do mic aceso sem sessão é o pior desfecho possível.
    liberarStream(_stream)
    _stream = null
    motorDeVoz.gravando = false
    motorDeVoz.modoDeVoz = false
    motorDeVoz.microfoneMutado = false
  }

  const _falharNaAbertura = (motivo, detalhe) => {
    _desmontar()
    _abrindo = false
    emitir('erro', { motivo, detalhe: detalhe || '' })
    throw new ErroDeVozTempoReal(motivo, detalhe)
  }

  /**
   * O cano (`-canal.js`) é quem fala WebRTC e HTTP. A trilha remota passa por
   * aqui em vez de ir direto para cima porque DUAS coisas a querem: a tela,
   * que a anexa a um `<audio>`, e o controle da fala ([T036]), que precisa
   * poder silenciá-la. Este módulo continua sem DOM.
   */
  const _cano = criarCanalWebRtc({
    aoReceberMensagem: (mensagem) => _leitor.aoReceberMensagem(mensagem),
    aoMudarConexao: (estado) => _aoMudarConexao(estado),
    aoReceberTrilha: (fluxo) => {
      _leitor.registrarTrilhaRemota(fluxo)
      emitir('audioRemoto', { stream: fluxo })
    },
    // ★ [T062] O canal abriu — é o primeiro instante em que dá para falar.
    aoAbrirCanal: () => _saudar(),
  })

  /**
   * ★ [T062] A IA abre a conversa. **Uma vez por sessão, e só no Modo de
   * Voz.**
   *
   * ★ **[T073] O mesmo append leva a regra de ACIONAR PRIMEIRO** — a etapa
   * (a) do desenho do owner, na redação que foi medida. Ela vale para os turnos SEGUINTES, e vai junto
   * porque instrução de sessão é contexto persistente: o que importa é estar
   * no contexto antes do primeiro pedido, e um segundo append custaria uma
   * mensagem a mais no fio para o mesmo efeito. Ver `_instrucoesDaAbertura`.
   *
   * - *uma vez*: `_saudou` é zerado na ABERTURA, não no fechamento. Reabrir
   *   depois de encerrar cumprimenta de novo (sessão nova, conversa nova);
   *   qualquer outro evento do meio da sessão encontra a marca já posta.
   * - *só no Modo de Voz*: o `MODO_DITAR` nunca chega a este transporte — o
   *   contrato manda ditado SEMPRE pelo `porTurnos` (`transporte-de-voz.js`),
   *   e ditar é pontual por natureza. Uma saudação num ditado seria a IA
   *   falando por cima de quem só queria transcrever uma frase.
   *
   * Best-effort, como todo o resto do canal: `_enviarNoCanal` devolve `false`
   * com o canal fechado e a sessão segue — o consultor perde o cumprimento,
   * nunca a sessão.
   */
  const _saudar = () => {
    if (_saudou) return
    _saudou = true
    _enviarNoCanal(
      montarAppendDeFala({
        tipo: EVENTO_APPEND_DE_INSTRUCOES,
        conteudo: _instrucoesDaAbertura(_nomeDoConsultor),
      })
    )
    // Sem este `response.create` a instrução fica no contexto e ninguém fala:
    // o modelo responde, não abre turno (§3 de `-remissoes.js`). Na abertura
    // não há `function_call` pendente, que é a única condição em que o
    // provedor o recusa (G-13 §3, sondagem D2).
    _enviarNoCanal(montarPedidoDeResposta())
  }

  /**
   * As duas checagens que vêm ANTES do microfone — as duas são de graça e não
   * abrem prompt nenhum. Negar o microfone para só então descobrir que o
   * navegador não fala WebRTC é pedir permissão por nada.
   */
  const _conferirPreRequisitos = (ctx) => {
    if (!suportaWebRtc()) {
      _falharNaAbertura(MOTIVO_SEM_SUPORTE, 'RTCPeerConnection indisponível.')
    }
    // Sem superfície não há como o servidor aplicar o gate do piloto (N13).
    // Supor `ia_operacional` seria abrir uma sessão PAGA declarando uma
    // origem que ninguém informou.
    if (!ctx.superficie) {
      _falharNaAbertura(MOTIVO_SEM_SUPERFICIE, 'Superfície não declarada.')
    }
  }

  /** ★ N9 — negado o microfone, nenhuma sessão é criada e nada é cobrado. */
  const _pedirMicrofone = async () => {
    try {
      _stream = await pedirMicrofoneComFallback()
    } catch (erro) {
      _abrindo = false
      _desmontar()
      const detalhe = (erro && erro.message) || ''
      emitir('erro', { motivo: MOTIVO_MICROFONE, detalhe })
      throw new ErroDeVozTempoReal(MOTIVO_MICROFONE, detalhe)
    }
  }

  // Estado de TURNO zerado a cada abertura: o buffer do consultor e o consumo
  // são por SESSÃO, e herdá-los de uma sessão anterior faria a primeira
  // transcrição nascer com a fala de outra conversa.
  const _prepararAbertura = (ctx) => {
    _abrindo = true
    _fechamentoPedido = false
    _superficie = ctx.superficie
    _delegar = typeof ctx.delegar === 'function' ? ctx.delegar : null
    // ★ [T065] Mesma régua do `delegar`: o que não é função não vira getter.
    _lerValorParcial =
      typeof ctx.lerValorParcialDoTurno === 'function'
        ? ctx.lerValorParcialDoTurno
        : null
    // ★ [T062] O nome e a marca da saudação são POR SESSÃO, como o resto
    // daqui: herdar a marca faria a segunda sessão nascer sem cumprimento, e
    // herdar o nome faria a saudação chamar o consultor anterior.
    _nomeDoConsultor = String(ctx.nomeDoConsultor || '')
    _saudou = false
    _leitor.reiniciar()
  }

  const _concluirAbertura = () => {
    _abrindo = false
    _abertaEm = _relogio()
    // [T035] O teto é o que o NOSSO servidor declarou na abertura — o
    // navegador o obedece, nunca o escolhe.
    _vigia.iniciar({ tetoSegundos: _tetoSegundos })
    // ★ [T068] Falar por cima CALA a IA. Armado aqui, e não na abertura do
    // microfone, porque só agora existe sessão para interromper — e desarmado
    // em `_desmontar`, junto com o stream que ele mede. O provedor não tem
    // onde ligar isso (medido: `session.audio` só aceita `output` e `format`),
    // e o fio não tem evento de cancelar fala: este é o único lugar possível.
    _escutaDeBargeIn = criarEscutaDeBargeInDoTempoReal({
      stream: _stream,
      iaEstaFalando: _leitor.iaFalandoAgora,
      aoDetectar: () => {
        _leitor.interromperFala()
        // ★★★ [T100] E ABRE A PORTA. O barge-in cala a IA em 0,04; o porteiro
        // só deixa o áudio sair em 0,15. Entre os dois havia uma faixa em que o
        // consultor cortava a IA e NÃO era ouvido — G4 de 19/09: calou 5/5
        // (mediana 482 ms), retomou **0/5**. Quem dispara aqui já decidiu, com
        // a IA falando por cima, que é o consultor; a porta obedece.
        if (_porteiro) _porteiro.abrirAgora()
        const banca = globalThis.__voz
        if (banca && Array.isArray(banca.microfone)) {
          banca.microfone.push({ evento: 'porteiro_aberto_por_barge_in', ms: Date.now() })
        }
      },
    })
    _escutaDeBargeIn.iniciar()
    // ★ [T080] O PORTEIRO: conversa de sala não vira turno pago. Medido no G5
    // — 30 janelas de 10 s com fundo a um DÉCIMO do volume da fala geraram 35
    // respostas espontâneas contra um teto de 2, e o número SUBIU quando o
    // fundo foi abaixado. O provedor não tem onde ligar isso (mesma medição da
    // [T068]: `session.audio` só aceita `output` e `format`), então o único
    // lugar onde a sala pode ser barrada é antes de o áudio sair do navegador.
    //
    // ⚠️ Armado DEPOIS do barge-in — e a ORDEM não é o que garante nada. Esta
    // nota afirmava que o porteiro abria no limiar mais BAIXO, e por isso a
    // interrupção chegaria ao provedor. Desde a [T095] é o contrário (porteiro
    // 0,15 · barge-in 0,04), e a garantia passou a ser a chamada explícita a
    // `abrirAgora()` no `aoDetectar` acima. `_porteiro` é lido lá DENTRO do
    // callback, então ele já existe quando alguém interrompe.
    // ★★★ [spec-008 T096] O porteiro está LIGADO — e agora por medição, não
    // por aposta.
    //
    // Ele nasceu desligado na [T086] porque eu o culpei de quebrar a
    // delegação em 4 corridas pagas. **Aquela acusação era falsa**: as
    // corridas rodaram com a fila do agente travada, e o sintoma era dela.
    // Medido depois, no ambiente sadio e com o porteiro ligado:
    // `controle_a_ia_respondeu: true` — a IA responde à fala direta.
    //
    // ⚠️ Mas ele também não consertava nada, e o motivo era o LIMIAR: abria em
    // 0,02 (herdado do `LIMIAR_VAD_RMS`) com o fundo medindo 0,05 a 0,12. Com
    // o limiar medido da [T095] (0,15), o G5 fechou:
    //
    // | | disparos em 30 janelas |
    // |---|---:|
    // | sem porteiro | **35** |
    // | porteiro a 0,02 | **44** |
    // | **porteiro a 0,15** | **0** |
    //
    // Com o controle passando nas três. ★ **G5: FAIL → PASS**, protocolo
    // completo.
    //
    // `window.__vozPorteiro = false` ainda DESLIGA — a porta de saída fica,
    // porque o limiar assume um fundo sintético e sala real pode desmenti-lo.
    if (globalThis.__vozPorteiro !== false) {
      _porteiro = criarPorteiroDoMicrofone({
        stream: _stream,
        estaMudo: () => Boolean(motorDeVoz.microfoneMutado),
        // ⚠️ Telemetria da BANCADA, não do produto: sem ela, uma sonda que não
        // mede o que esperava não distingue "a IA não entendeu" de "o porteiro
        // cortou a fala". Peça acessória precisa poder ser inocentada por DADO.
        aoMudar: (passando) => {
          const banca = globalThis.__voz
          if (!banca || !Array.isArray(banca.microfone)) return
          banca.microfone.push({
            evento: passando ? 'porteiro_abriu' : 'porteiro_fechou',
            ms: Date.now(),
          })
        },
      })
      _porteiro.iniciar()
    }
    motorDeVoz.gravando = true
    motorDeVoz.modoDeVoz = true
    motorDeVoz.microfoneMutado = false
  }

  /**
   * Abre a sessão. **CONTRATO DE ORDEM (N9): o microfone é pedido ANTES de
   * qualquer recurso pago** — nada de `RTCPeerConnection`, nada de
   * `POST /voz/sessao-tempo-real`, antes de o consultor ter dito sim. Negado o
   * microfone, nenhuma sessão é criada e nada é cobrado.
   *
   * Rejeita com `ErroDeVozTempoReal` (`.motivo` nomeado) — quem chama decide a
   * queda para `porTurnos` (US-3 AC5, [T034]).
   *
   * @param {Object} ctx `ContextoDeSessao` (`contracts/transporte.md`) mais
   *   `superficie`, que a rota exige e o contrato não listava (C-5: a
   *   superfície viaja em toda chamada, não só na abertura).
   */
  const abrir = async (ctx = {}) => {
    if (_cano.ativo() || _abrindo) return
    _conferirPreRequisitos(ctx)
    _prepararAbertura(ctx)
    await _pedirMicrofone()
    try {
      _cano.montar(_stream)
      const sessaoAberta = await _cano.negociar(ctx)
      _idSessao = sessaoAberta.idSessao
      _modelo = sessaoAberta.modelo
      _tetoSegundos = sessaoAberta.tetoSegundos
    } catch (erro) {
      // Um caminho só para toda falha de abertura: desmonta, solta o
      // microfone, emite `erro` NOMEADO e rejeita. O motivo do cano é
      // preservado quando ele nomeou um; senão, é sessão recusada.
      _falharNaAbertura(
        (erro && erro.motivo) || MOTIVO_SESSAO_RECUSADA,
        (erro && erro.message) || ''
      )
    }
    _concluirAbertura()
  }

  /**
   * Os segundos que vão ao encerramento. **A fonte autoritativa é o ledger
   * NATIVO do provedor** (`session.usage.updated`, medido em todas as 7
   * sessões) — não o relógio de parede, que conta o tempo de negociação e de
   * teardown como se fosse áudio. O relógio só entra quando a sessão morreu
   * antes do primeiro tique de uso (~15 s), e aí ele é a única medida que
   * existe; zero ali diria "encerrou sem consumir", que é um fato diferente.
   */
  const _segundosCobraveis = () => {
    const doProvedor = _leitor.segundosDoProvedor()
    if (typeof doProvedor === 'number') return doProvedor
    if (typeof _abertaEm !== 'number') return 0
    return (_relogio() - _abertaEm) / 1000
  }

  /**
   * [T032] O ÚNICO ponto de saída para o fio — o `session.close` e o laço da
   * delegação passam os dois por aqui. Best-effort por desenho: com o canal
   * caído não há a quem enviar, e o servidor encerra por TTL de qualquer
   * forma.
   */
  /**
   * ★ [T033] O ponto ÚNICO de saída para o fio — e por isso o lugar certo da
   * guarda de D-13 sobre a FALA. `conferirQueRemete` trava o resultado da
   * delegação; esta trava o `commentary.append`, que é o outro caminho até a
   * boca do modelo (e o que um dev futuro usaria para "fazer a voz avisar o
   * valor"). Estrutural: não existe rota até a fala que não passe por aqui.
   */
  const _enviarNoCanal = (mensagem, origem) => {
    // ★ [T063, correção do owner] `origem` é `{valor, tipo}` — o que a consulta
    // JÁ devolveu — e só existe na fala que adianta número. Ela atravessa até
    // aqui em vez de ser conferida lá atrás porque a guarda tem de morar no
    // ponto ÚNICO de saída: conferir no produtor deixaria o ponto de saída
    // aceitando qualquer string, que é o oposto de estrutural.
    conferirQueAFalaRemete(mensagem, origem)
    return _cano.enviar(mensagem)
  }

  const _esquecerSessao = () => {
    _idSessao = null
    _modelo = null
    _tetoSegundos = null
    _superficie = null
    _delegar = null
    // ★ [T065] Sai com a sessão, como o `delegar`: ele lê o store de UMA
    // conversa, e herdá-lo faria a sessão seguinte adiantar o número da
    // anterior — um valor correto atribuído ao pedido errado (N16).
    _lerValorParcial = null
    _abertaEm = null
    // ★ [T062] O nome sai com a sessão — ele é dado de quem estava falando.
    // `_saudou` NÃO é zerado aqui de propósito: quem o zera é a abertura
    // seguinte, e é essa assimetria que garante "uma vez por sessão" mesmo se
    // o canal abrir tarde, depois de um `fechar` já pedido.
    _nomeDoConsultor = ''
  }

  /**
   * Fecha a sessão. **Idempotente**: chamar duas vezes solta o microfone uma
   * vez e registra o consumo uma vez — o guard de entrada vê a sessão já
   * esquecida e sai. O servidor também é idempotente (encerrar duas vezes
   * devolve o mesmo total, nunca soma), então nem a corrida entre os dois
   * lados soma consumo.
   *
   * A ORDEM importa: o consumo é lido ANTES do teardown, porque o teardown
   * apaga o instante de abertura — e o `session.close` sai ANTES de fechar o
   * canal, porque depois não há por onde pedir.
   */
  const fechar = async () => {
    if (!_cano.ativo() && !_idSessao) return
    _fechamentoPedido = true
    _leitor.encerrarFalaDaIa()
    const idSessao = _idSessao
    const segundos = _segundosCobraveis()
    _enviarNoCanal({ type: EVENTO_FECHAR_SESSAO })
    _desmontar()
    _esquecerSessao()
    if (!idSessao) return
    const r = await apiVoz.encerrarSessaoTempoReal({
      idSessao,
      segundosCobraveis: segundos,
    })
    if (!r || r.status !== 200) {
      emitir('erro', {
        motivo: MOTIVO_SESSAO_RECUSADA,
        detalhe: (r && r.message) || '',
      })
    }
  }

  /**
   * ★ [T035] O corte por limite — teto de duração ou foco perdido.
   *
   * Passa pelo `fechar` de SEMPRE, que é quem manda `session.close`, solta o
   * microfone e registra a duração cobrável no nosso servidor: um caminho de
   * encerramento paralelo teria de repetir as três coisas, e é assim que uma
   * delas fica para trás.
   *
   * O `encerrada` sobe ANTES do `fechar` porque a tela precisa parar de
   * desenhar sessão de voz no mesmo instante — e carrega os segundos LIDOS
   * antes do teardown, que apaga o instante de abertura.
   *
   * ⚠️ `encerrada` e `avisoDeEncerramento` **não estão em
   * `contracts/transporte.md`** — a tabela do `Destino` não tem como dizer
   * "encerrei sozinho" nem "vou encerrar". A divergência está reportada.
   */
  const _encerrarPorLimite = (motivo) => {
    if (!_cano.ativo() && !_idSessao) return
    emitir('encerrada', { motivo, segundosCobraveis: _segundosCobraveis() })
    fechar()
  }

  /**
   * ★ [T036] O consultor quer falar por cima. **Não fala no fio, e isso é o
   * contrato** (N4): quem decide o turno é o provedor, e o protocolo medido
   * não tem evento de cancelar fala — a lista cliente→servidor inteira está no
   * cabeçalho. Este transporte NÃO reimplementa barge-in: falar por cima já
   * chega ao provedor pelo microfone, que segue aberto.
   *
   * O que ele faz é LOCAL e são dois dos três estados da D-11: **para a fala**
   * (silencia a trilha remota) e **descarta o texto** da fala corrente. O
   * terceiro — o job — **segue**, e é o invariante N17: *"como está indo?"*
   * não mata a consulta que o consultor está perguntando sobre. Quem mata é
   * `cancelarTrabalho()`.
   */
  const interromper = () => {
    _leitor.interromperFala()
  }

  /**
   * ★ [T036] O cancelamento EXPLÍCITO do job em voo — o único caminho que
   * aborta a delegação a pedido do consultor (D-11).
   *
   * ⚠️ **Não está em `contracts/transporte.md`**, que só tem `interromper`.
   * Ele nasce da correção da D-11 na iteração 3: se o mesmo verbo parasse a
   * fala e matasse o job, o caso central da spec ficaria impossível de
   * exprimir. A divergência está reportada — o contrato precisa do verbo.
   */
  const cancelarTrabalho = () => {
    _leitor.cancelarTrabalho()
  }

  /**
   * Mudo. Desliga a TRACK local, não um evento de protocolo: assim o áudio não
   * sai do navegador, em vez de sair e ser descartado do outro lado. (O
   * provedor também aceita `session.input_audio.mute`/`.unmute` — medido —,
   * mas ele depende do canal estar vivo e não impede o envio.)
   */
  const alternarMudo = (mudo) => {
    if (!_stream) return
    const silenciar = Boolean(mudo)
    motorDeVoz.microfoneMutado = silenciar
    _stream.getAudioTracks().forEach((track) => {
      track.enabled = !silenciar
    })
  }

  /** Leitura só para diagnóstico/medição — nunca para decidir comportamento. */
  const sessao = () => ({
    idSessao: _idSessao,
    modelo: _modelo,
    tetoSegundos: _tetoSegundos,
    segundosDoProvedor: _leitor.segundosDoProvedor(),
  })

  return {
    nome: TRANSPORTE_TEMPO_REAL,

    // Contrato (`contracts/transporte.md`).
    abrir,
    fechar,
    interromper,
    alternarMudo,
    registrarDestino,

    // [T036] Fora do contrato, e reportado: `interromper` para a FALA, este
    // mata o JOB. Um verbo só para as duas coisas é a D-11 original, que a
    // iteração 3 derrubou.
    cancelarTrabalho,

    // Simetria com o `porTurnos` — `send-message.vue` desregistra no
    // `beforeDestroy`, e o guard por identidade evita apagar o destino que a
    // instância seguinte acabou de registrar.
    desregistrarDestino,
    sessao,
  }
}

/**
 * [T030] O registro no ponto único de seleção, na carga do módulo — fábrica,
 * não instância: o transporte só nasce quando de fato selecionado.
 *
 * ★ **[T030b] Quem carrega este arquivo é `motor-de-voz.js`**, com um
 * `import './transporte-tempo-real'` sem bindings — é ele que faz o registro
 * abaixo acontecer. NUNCA em `transporte-de-voz.js`, que este arquivo
 * importa: seria um ciclo, e o `registrarTransporteTempoReal` cairia em zona
 * morta no meio da carga.
 *
 * Carregado ≠ selecionado: no Ditar, na queda do [T034] e em composer sem
 * `superficie` declarada, `selecionarTransporte` continua devolvendo
 * `porTurnos` — ver o cabeçalho de `transporte-de-voz.js` ([T046], D-O-4).
 */
registrarTransporteTempoReal(criarTransporteTempoReal)
